import { Fragment, useCallback, useEffect, useRef, useState, type FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { altDealStructureTermDownPaymentUpdated } from '@makemydeal/dr-dash-store/actions/deskingActionCreators';
import * as vehicleSelectors from '@makemydeal/dr-dash-store/selectors/vehicleSelectors';
import { buildDealStructurePredictionsFromSlots } from '../altDealStructureCdlUtils';
import { Typography } from '@interstate/components/Typography';
import { ArrowLongRightIcon } from '@interstate/components/Icons/ArrowLongRightIcon';
import { ArrowSmallDownIcon } from '@interstate/components/Icons/ArrowSmallDownIcon';
import { AiLogoIcon } from '@makemydeal/dr-shared-components';
import { PaymentStructureGridCell } from './PaymentStructureGridCell';
import { TermColumnHeader } from './TermColumnHeader';
import {
    CornerLabel,
    CornerLabelText,
    GridCardHeader,
    GridLayout,
    HeaderDivider,
    HeaderInputCell,
    StyledGridCard
} from './PaymentStructureGrid.styles';
import type { Slot } from './types';
import { DownPaymentRowInput } from '../components/DownPaymentRowInput';
import { useGridActions } from '../contexts';

interface PaymentStructureGridProps {
    title: string;
    terms: number[];
    downPayments: number[];
    slots: Slot[];
    dealType: 'finance' | 'lease';
    testIdPrefix?: string;
    onTermsCommit?: (terms: number[]) => void;
    onDownPaymentsCommit?: (downPayments: number[]) => void;
}

const findSlot = (slots: Slot[], term: number, downPayment: number): Slot | undefined =>
    slots.find((s) => s.term === term && s.downPayment === downPayment);

export const PaymentStructureGrid: FC<PaymentStructureGridProps> = ({
    title,
    terms,
    downPayments,
    slots,
    dealType,
    testIdPrefix = 'payment-structure-grid',
    onTermsCommit,
    onDownPaymentsCommit
}) => {
    const theme = useInterstateTheme();
    const dispatch = useDispatch();
    const vehicleInfo = useSelector(vehicleSelectors.getVehicleAnalyticsPayload);

    // Grid action context: isAtMaxScenarios makes the term/down inputs read-only (no structure can be
    // chosen); markEditedDownPayment records each user-typed down payment for the retry-banner logic.
    const { isAtMaxScenarios, markEditedDownPayment } = useGridActions();

    // Index of the last term column / down-payment row the user edited. Only that axis value is flagged
    // "Invalid" when it comes back empty — parity with the main grid, which flags only its lastUpdatedInput.
    const [lastEditedTermIndex, setLastEditedTermIndex] = useState<number | null>(null);
    const [lastEditedDownPaymentIndex, setLastEditedDownPaymentIndex] = useState<number | null>(null);

    // True when a term's column has finished loading with no rates in any row — Payment Service returned
    // nothing for that term (the server-driven equivalent of the main grid's PAYMENT_FAILURE highlight).
    const termHasNoRates = useCallback(
        (term: number): boolean => {
            const termSlots = slots.filter((s) => s.term === term);
            if (termSlots.length === 0 || termSlots.some((s) => s.status === 'pending')) return false;
            return termSlots.every((s) => s.status !== 'ready');
        },
        [slots]
    );

    // True when a down-payment row has finished loading with no rates in any term — the row-level
    // counterpart of termHasNoRates.
    const downPaymentRowHasNoRates = useCallback(
        (downPayment: number): boolean => {
            const rowSlots = slots.filter((s) => s.downPayment === downPayment);
            if (rowSlots.length === 0 || rowSlots.some((s) => s.status === 'pending')) return false;
            return rowSlots.every((s) => s.status !== 'ready');
        },
        [slots]
    );

    // dc-mv-292 must fire *after* the edited terms/cash have recalculated (grid slots refetched), not on
    // commit — otherwise the presented-structures payload is built from the pre-edit slots. We flag that an
    // edit is awaiting its event, note that a recalc was seen in-flight (slots pending), then fire once the
    // slots settle back to a non-pending state so the event carries the recalculated values.
    const awaitingRecalcFireRef = useRef(false);
    const sawRecalcRef = useRef(false);
    const anyPending = slots.some((s) => s.status === 'pending');

    useEffect(() => {
        if (!awaitingRecalcFireRef.current) return;
        if (anyPending) {
            sawRecalcRef.current = true;
            return;
        }
        if (sawRecalcRef.current) {
            // dc-mv-292: the presented structures for this deal type (the grid's recalculated ready slots).
            dispatch(altDealStructureTermDownPaymentUpdated(vehicleInfo, buildDealStructurePredictionsFromSlots(slots)));
            awaitingRecalcFireRef.current = false;
            sawRecalcRef.current = false;
        }
    }, [anyPending, slots, dispatch, vehicleInfo]);

    const queueTermDownPaymentAnalytics = useCallback(() => {
        awaitingRecalcFireRef.current = true;
    }, []);

    const handleTermCommit = useCallback(
        (index: number) => (newTerm: number) => {
            if (!onTermsCommit) return;
            const next = [...terms];
            next[index] = newTerm;
            setLastEditedTermIndex(index);
            onTermsCommit(next);
            queueTermDownPaymentAnalytics();
        },
        [terms, onTermsCommit, queueTermDownPaymentAnalytics]
    );

    const handleDownPaymentCommit = useCallback(
        (index: number, newValue: number) => {
            if (!onDownPaymentsCommit) return;
            const next = [...downPayments];
            next[index] = newValue;
            setLastEditedDownPaymentIndex(index);
            markEditedDownPayment(dealType, newValue);
            onDownPaymentsCommit(next);
            queueTermDownPaymentAnalytics();
        },
        [downPayments, onDownPaymentsCommit, queueTermDownPaymentAnalytics, markEditedDownPayment, dealType]
    );

    return (
        <StyledGridCard theme={theme} data-testid={testIdPrefix}>
            <GridCardHeader>
                <Typography variant="h5" data-testid={`${testIdPrefix}-title`}>
                    {title}
                </Typography>
                <AiLogoIcon fontSize="20px" ariaHidden={true} data-testid={`${testIdPrefix}-ai-logo`} />
            </GridCardHeader>

            <HeaderDivider theme={theme} />

            <GridLayout $columns={terms.length} $rows={downPayments.length} data-testid={`${testIdPrefix}-layout`}>
                <CornerLabel theme={theme} data-testid={`${testIdPrefix}-corner`}>
                    <CornerLabelText theme={theme} variant="body-xs">
                        TERM <ArrowLongRightIcon fontSize="14px" />
                    </CornerLabelText>
                    <CornerLabelText theme={theme} variant="body-xs">
                        CASH <ArrowSmallDownIcon fontSize="14px" />
                    </CornerLabelText>
                </CornerLabel>

                {terms.map((term, i) => (
                    <HeaderInputCell key={`term-${term}`}>
                        <TermColumnHeader
                            term={term}
                            allTerms={terms}
                            onTermChange={handleTermCommit(i)}
                            isLoading={slots.some((s) => s.term === term && s.status === 'pending')}
                            hasError={i === lastEditedTermIndex && termHasNoRates(term)}
                            readOnly={isAtMaxScenarios}
                            testId={`${testIdPrefix}-term-${term}`}
                        />
                    </HeaderInputCell>
                ))}

                {downPayments.map((downPayment, rowIndex) => (
                    <Fragment key={`row-${downPayment}`}>
                        <HeaderInputCell>
                            <DownPaymentRowInput
                                downPayment={downPayment}
                                rowIndex={rowIndex}
                                allDownPayments={downPayments}
                                onDownPaymentChange={handleDownPaymentCommit}
                                isLoading={slots.some((s) => s.downPayment === downPayment && s.status === 'pending')}
                                hasError={rowIndex === lastEditedDownPaymentIndex && downPaymentRowHasNoRates(downPayment)}
                                readOnly={isAtMaxScenarios}
                                testId={`${testIdPrefix}-down-payment-${downPayment}`}
                            />
                        </HeaderInputCell>
                        {terms.map((term) => {
                            const slot = findSlot(slots, term, downPayment);
                            if (!slot) {
                                return (
                                    <div
                                        key={`empty-${term}-${downPayment}`}
                                        data-testid={`${testIdPrefix}-empty-${term}-${downPayment}`}
                                    />
                                );
                            }
                            return <PaymentStructureGridCell key={slot.id} slot={slot} dealType={dealType} />;
                        })}
                    </Fragment>
                ))}
            </GridLayout>
        </StyledGridCard>
    );
};
